import React, { useRef, useState } from 'react';

import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import HookForm from '../HookForm/HookForm';
import LoaderInline from '../Loader/LoaderInline';
import Datepicker from '../Inputs/Datepicker/Datepicker';
import SelectClientID from '../Selects/SelectClientD';
import SelectClientIssuer from '../Selects/SelectClientDocumentType';
import ClientCreatePkb from '../../pages/Client/NewClient/ClientCreatePkb/ClientCreatePkb';
import ClientCreate from '../../pages/Client/NewClient/ClientCreate/ClientClreate';
import ClientFcbDocument from '../../pages/Client/ClientMainData/ClientFcbDocument/ClientFcbDocument';
import getClients from '../../api/clients/getClients';
import editClientDocuments from '../../api/clients/editClientDocuments';
import alert from '../../helpers/alert';
import { colors } from '../../constants/colors';
import { CLIENT_DOCUMENT_TYPE, CLIENT_DOCUMENT_ISSUED_BY } from '../../constants';

// Метка как в SelectClients — чтобы созданный клиент выглядел в селекте так же,
// как подгруженные с бэка.
const toOption = (client) => ({
  value: client?.id,
  label: `${client?.name} ${client?.surname}` + (client?.iin ? ` (ИИН: ${client.iin})` : ''),
  client,
});

const linkStyle = {
  display: 'inline-block',
  color: colors.cameo,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const fetchClient = (clientId) =>
  getClients({ id: clientId }).then((res) => res?.data?.users?.[0] || null);

// В карточке орган хранится кодом, а ПКБ присылает его названием
// («МИНИСТЕРСТВО ВНУТРЕННИХ ДЕЛ РК») — приводим к справочнику.
const resolveIssuer = (raw) => {
  if (raw === null || raw === undefined || raw === '') return undefined;

  const byCode = CLIENT_DOCUMENT_ISSUED_BY.find((item) => item.value === Number(raw));
  if (byCode) return byCode;

  const text = String(raw).toLowerCase();
  if (text.includes('внутренн') || text.includes('мвд')) return CLIENT_DOCUMENT_ISSUED_BY[0];
  if (text.includes('юстиц') || text.includes('мю')) return CLIENT_DOCUMENT_ISSUED_BY[1];
  return undefined;
};

const buildDocumentInputs = (client) => [
  {
    component: SelectClientID,
    name: 'document_type_id',
    rules: { required: true },
    defaultValue: CLIENT_DOCUMENT_TYPE.find((item) => item.value === client?.document?.document_type_id),
  },
  {
    name: 'document_number',
    label: 'Номер документа',
    rules: { required: true },
    defaultValue: client?.document?.document_number,
  },
  {
    component: SelectClientIssuer,
    name: 'document_issuer',
    label: 'Кем выдан',
    rules: { required: true },
    defaultValue: resolveIssuer(client?.document?.document_issuer),
  },
  {
    component: Datepicker,
    name: 'document_date',
    type: 'date',
    label: 'Дата выдачи документа',
    rules: { required: true },
    defaultValue: client?.document?.document_date,
  },
  {
    component: Datepicker,
    name: 'document_date_end',
    type: 'date',
    label: 'Срок действия документа',
    rules: { required: true },
    defaultValue: client?.document?.document_date_end,
  },
];

/**
 * Модалка «Добавить клиента» — создание клиента, не уходя с текущего экрана.
 * Открывается на получении данных с eGov (ГБД ФЛ), оттуда есть ссылка на ручную форму.
 *
 * ГБД отдаёт только персданные и адрес, документов в её ответе нет, поэтому после
 * создания через eGov идёт второй шаг: запрос цифрового удостоверения (FCB ID OTP,
 * отдельная СМС) и обязательная проверка полей — подтянутое можно поправить, а если
 * получить не удалось, тут же заполнить руками. После ручной формы шага нет:
 * документ там менеджер уже ввёл.
 *
 * onCreated получает опцию для селекта: { value, label, client }.
 */
const ClientCreateModal = ({ isOpen, onRequestClose, onCreated }) => {
  const [mode, setMode] = useState('egov');
  const [stage, setStage] = useState('create'); // create | document
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  // Форма читает defaultValues один раз при монтировании, поэтому после
  // подтягивания документа пересоздаём её через key.
  const [documentVersion, setDocumentVersion] = useState(0);
  const documentFormRef = useRef(null);

  const reset = () => {
    setStage('create');
    setCreated(null);
    setDocumentVersion(0);
  };

  // Клиент уже создан на бэке: даже если менеджер закрыл модалку крестиком,
  // отдаём его наверх — иначе он заведёт дубль.
  const closeWithCreated = () => {
    if (created) onCreated(toOption(created));
    reset();
    onRequestClose();
  };

  const finish = async (clientId) => {
    setBusy(true);
    try {
      const client = await fetchClient(clientId);
      onCreated(client ? toOption(client) : { value: clientId, label: `Клиент #${clientId}` });
    } catch (e) {
      onCreated({ value: clientId, label: `Клиент #${clientId}` });
    } finally {
      setBusy(false);
      reset();
      onRequestClose();
    }
  };

  const handleCreated = async (clientId, withDocumentStep) => {
    if (!clientId) {
      alert.error('Клиент создан, но не удалось получить его карточку — выберите его в списке вручную');
      reset();
      onRequestClose();
      return;
    }

    if (!withDocumentStep) return finish(clientId);

    setBusy(true);
    let client = null;
    try {
      client = await fetchClient(clientId);
    } catch (e) {
      client = null;
    }
    setBusy(false);

    // Без карточки шаг с документом не собрать — нужны ИИН и телефон.
    if (!client) return finish(clientId);

    setCreated(client);
    setStage('document');
  };

  // ПКБ дописал документ на бэке — перечитываем карточку и пересобираем форму.
  const reloadDocument = async () => {
    try {
      const client = await fetchClient(created?.id);
      if (client) {
        setCreated(client);
        setDocumentVersion((prev) => prev + 1);
      }
    } catch (e) {
      // не страшно: поля остаются заполняемыми вручную
    }
  };

  const saveDocument = async () => {
    const values = await documentFormRef.current.submit();
    if (!values) return;

    setBusy(true);
    try {
      await editClientDocuments({
        id: created?.id,
        document_type_id: values?.document_type_id?.value,
        document_number: values?.document_number,
        document_issuer: values?.document_issuer?.value,
        document_date: values?.document_date,
        document_date_end: values?.document_date_end,
      });
    } catch (e) {
      setBusy(false);
      alert.error('Не удалось сохранить данные документа');
      return;
    }
    setBusy(false);
    finish(created?.id);
  };

  const renderCreate = () =>
    mode === 'egov' ? (
      <ClientCreatePkb onCreated={(id) => handleCreated(id, true)} onManual={() => setMode('manual')} />
    ) : (
      <>
        <span style={{ ...linkStyle, marginBottom: 16 }} role="button" onClick={() => setMode('egov')}>
          ← Получить данные с eGov
        </span>
        <ClientCreate onCreated={(id) => handleCreated(id, false)} hidePhoto />
      </>
    );

  const renderDocument = () => (
    <>
      <p style={{ marginBottom: 16, color: colors.dimGray }}>
        Клиент {created?.surname} {created?.name} создан. Осталось внести данные удостоверения
        личности — запросите их у клиента по SMS или заполните вручную.
      </p>

      <ClientFcbDocument
        clientId={created?.id}
        iin={created?.iin}
        phone={created?.phone}
        onSuccess={reloadDocument}
      />

      <HookForm
        key={documentVersion}
        ref={documentFormRef}
        border
        title="Данные удостоверения"
        inputs={buildDocumentInputs(created)}
        grid={{ display: 'grid', gap: 20, gridTemplateColumns: '1fr 1fr' }}
        style={{ marginBottom: 0 }}
      />

      <Button text="Сохранить и продолжить" fill onClick={saveDocument} />
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeWithCreated}
      title="Добавить клиента"
      width={mode === 'manual' && stage === 'create' ? '1100px' : '720px'}
      contentStyle={{ bottom: 'auto', height: 'auto', maxHeight: '90vh' }}
    >
      {busy ? <LoaderInline /> : stage === 'document' ? renderDocument() : renderCreate()}
    </Modal>
  );
};

export default ClientCreateModal;
